import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";

import * as store from "../state/store.js";
import { SignalWatcher } from "../state/signal-watcher.js";
import { openModal } from "../ops/ui.js";
import { tooltip } from "../utils/tooltip.js";

@customElement("status-bar")
export class StatusBar extends SignalWatcher(LitElement) {
  static override styles = css`
    :host {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: var(--overlay-bg);
      color: var(--text-secondary);
      padding: 3px 16px;
      font-family: var(--font-body);
      font-size: 11px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      border-top: 1px solid var(--border-color);
      z-index: 2;
      /* Never wrap — a two-line status bar eats vertical space the 3D view
         needs. On very narrow phones the right group can scroll horizontally
         inside its own container if it overflows, but the whole bar stays
         a single 22-ish pixel strip. */
      white-space: nowrap;
    }

    .left, .right {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
    }

    .sep {
      opacity: 0.35;
    }

    .clickable {
      cursor: pointer;
      padding: 1px 6px;
      border-radius: 3px;
      color: var(--text-primary);
      transition: background 0.15s;
    }

    .clickable:hover { background: var(--bg-hover); }

    /* Compact labels are only shown at mobile widths; desktop keeps the full
       "Drawing Units" phrasing so the strip reads naturally. */
    .full-label { display: inline; }
    .short-label { display: none; }

    /* Mobile: shrink everything and abbreviate labels so both groups fit on
       one line at iPhone SE (375px) widths. The left group (entity / layer /
       selection counts) becomes a single compact segment; the right group's
       "Drawing Units" / "Editor Units" collapse to "Dwg" / "Ed". Anything
       still left over overflows via a horizontal scroll on the right group
       instead of wrapping the whole strip. */
    @media (max-width: 720px) {
      :host {
        font-size: 10px;
        padding: 3px 8px;
        gap: 6px;
      }
      .left, .right { gap: 6px; }
      .right {
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: none;
      }
      .right::-webkit-scrollbar { display: none; }
      .clickable {
        padding: 1px 4px;
      }
      .full-label { display: none; }
      .short-label { display: inline; }
      .sep { margin: 0 -2px; }
      /* Layer / selection counts are visible in the layer panel — drop them
         from the status bar on mobile so the right group (which units +
         grid ARE always relevant) has room to render inline. */
      .left-detail { display: none; }
    }
  `;

  override render() {
    // The left group (entity / layer / selection counts) is only meaningful
    // against the 2D drawing — hide it in 3D since the extruded preview has
    // no notion of "selected entities". Zoom % is dropped in both modes.
    const in3D = store.viewMode() === "3d";
    const loaded = store.isLoaded();
    const total = store.entities().length;
    const layers = store.layerOrder().length;
    const selected = store.selectedEntityIds().size;
    const drawingUnit = store.dxfUnit();
    const editorUnit = store.viewerUnit();
    const gridSpacing = store.gridSpacing();
    const gridUnit = store.gridUnit();

    return html`
      <div class="left">
        ${in3D
          ? ""
          : html`
              <span>${loaded ? `${total} entities` : "Ready"}</span>
              ${loaded
                ? html`
                    <span class="sep left-detail">|</span>
                    <span class="left-detail">Layers: ${layers}</span>
                    <span class="sep left-detail">|</span>
                    <span class="left-detail">Selected: ${selected}</span>
                  `
                : ""}
            `}
      </div>
      <div class="right">
        <span
          class="clickable"
          ${tooltip("Change Units & Grid Settings")}
          @click=${this.#openSettings}
        >
          <span class="full-label">Drawing Units: ${drawingUnit}</span>
          <span class="short-label">Dwg: ${drawingUnit}</span>
        </span>
        <span class="sep">|</span>
        <span
          class="clickable"
          ${tooltip("Change Units & Grid Settings")}
          @click=${this.#openSettings}
        >
          <span class="full-label">Editor Units: ${editorUnit}</span>
          <span class="short-label">Ed: ${editorUnit}</span>
        </span>
        <span class="sep">|</span>
        <span
          class="clickable"
          ${tooltip("Change Units & Grid Settings")}
          @click=${this.#openSettings}
          >Grid: ${gridSpacing} ${gridUnit}</span
        >
      </div>
    `;
  }

  #openSettings = () => openModal("unit-settings", {});
}

declare global {
  interface HTMLElementTagNameMap {
    "status-bar": StatusBar;
  }
}
